import { useCallback, useEffect, useMemo, useRef } from "react";
import { type ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useStore } from "../../store";
import { createThreadSelectorByRef } from "../../storeSelectors";
import {
  buildComposerPromptHistoryEntries,
  stepComposerPromptHistory,
  type ComposerPromptHistoryPosition,
} from "./composerPromptHistory";

/**
 * Terminal-style prompt recall for the composer: ArrowUp on an empty draft
 * walks back through the active thread's sent prompts, ArrowDown walks
 * forward and restores the unsent draft. Returns true when a key was
 * consumed, false so normal caret movement happens.
 *
 * Attachments held by the composer block recall the same way upstream blocks them.
 */

interface RecallKeyEvent {
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly isComposing?: boolean;
}

/** Leaves modified arrow keys and IME composition to the editor. */
function hasRecallModifiers(event: RecallKeyEvent): boolean {
  return (
    event.shiftKey || event.altKey || event.metaKey || event.ctrlKey || event.isComposing === true
  );
}

/** Blocks recall for disabled input, attachments, or a typed draft outside history browsing. */
function isRecallBlocked(input: {
  readonly disabled: boolean;
  readonly hasAttachments: boolean;
  readonly position: ComposerPromptHistoryPosition | null;
  readonly currentPrompt: string;
}): boolean {
  if (input.disabled) return true;
  if (input.hasAttachments) return true;
  // A typed draft with no active recall can never step, so skip the entry
  // build for that common case.
  return input.position === null && input.currentPrompt.length > 0;
}

/** Returns a history navigation handler that consumes valid recall keys for the active thread. */
export function usePromptHistoryRecall(input: {
  readonly environmentId: import("@cmmd-forge/contracts").EnvironmentId;
  readonly activeThreadId: import("@cmmd-forge/contracts").ThreadId | null;
  readonly promptRef: { current: string };
  readonly hasAttachments: boolean;
  /** True while an approval, question, or other overlay owns the composer. */
  readonly disabled: boolean;
  readonly applyPrompt: (nextPrompt: string) => void;
}): {
  navigate: (direction: "backward" | "forward", event: RecallKeyEvent) => boolean;
  isActive: () => boolean;
} {
  const { environmentId, activeThreadId, promptRef, hasAttachments, disabled, applyPrompt } = input;

  const threadRef: ScopedThreadRef | null = useMemo(
    () => (activeThreadId === null ? null : scopeThreadRef(environmentId, activeThreadId)),
    [environmentId, activeThreadId],
  );

  const selectThread = useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]);
  const thread = useStore(selectThread);

  const positionRef = useRef<ComposerPromptHistoryPosition | null>(null);

  // A thread switch orphans the position; browsing restarts from scratch.
  useEffect(() => {
    positionRef.current = null;
  }, [threadRef]);

  const navigate = useCallback(
    (direction: "backward" | "forward", event: RecallKeyEvent) => {
      if (hasRecallModifiers(event)) return false;
      if (
        isRecallBlocked({
          disabled,
          hasAttachments,
          position: positionRef.current,
          currentPrompt: promptRef.current,
        })
      ) {
        return false;
      }
      const entries = buildComposerPromptHistoryEntries(
        (thread?.messages ?? []).map((message) => ({
          id: message.id,
          role: message.role,
          text: message.text,
        })),
      );
      const step = stepComposerPromptHistory({
        direction,
        entries,
        position: positionRef.current,
        currentPrompt: promptRef.current,
      });
      if (!step) return false;
      positionRef.current = step.position;
      applyPrompt(step.prompt);
      return true;
    },
    [applyPrompt, disabled, hasAttachments, promptRef, thread],
  );
  const isActive = useCallback(
    () => positionRef.current !== null && positionRef.current.recalled === promptRef.current,
    [promptRef],
  );
  return { navigate, isActive };
}
